import { useCallback, useMemo, useState } from "react";
import { COMPANY_ROLE_ADMIN, type CompanyRole } from "./company-roles";
import { MAX_CONTACT_PERSONS, type UseCompanyAdminResult } from "./use-company-admin";

// The member roster as a DRAFT: every change an admin makes is staged and nothing reaches genesis
// until the SaveBar saves the scope.
//
// WHY THIS EXISTS. Until now the add form was the only staged thing on this panel — a picked person
// was unsaved work, and everything else (add, demote, unmark, remove) wrote on the click. So an
// admin curating a roster produced a stream of individual writes, each already applied and each
// separately undoable only by making the opposite change, while the About-us box two sections up
// waited politely for Save. Kevin asked for one behaviour across the whole edit view: stage
// everything, show it as a preview, apply it when Save is pressed.
//
// FOUR KINDS OF INTENT, AND THEY COMPOSE. `added` carries people not in the company yet; `roles`,
// `contacts` and `removed` carry changes to rows that exist. They are separate maps rather than one
// list of patches because they are separate genesis mutations with separate refusals, and because
// the order they are applied in matters (see `save`).
//
// THE PROJECTION IS WHAT THE SCREEN READS. `projected` folds the intents over the stored rows, so
// the roster shows the state as it WILL be, with each changed row saying so in words. That is the
// same shape the import previews use, and the reason the caps below are computed from the projection
// rather than from what genesis currently holds: an admin who has staged two removals and one add
// must be told about the roster they are building, not the one they started from.
//
// WHAT IS NOT STAGED: the search text and the current pick in the add form. Those are the form's own
// working state — `stageAdd` is what turns a pick into an intent — and a half-typed name is not work
// worth guarding.

/** A member row as genesis holds it, narrowed to what the draft needs. */
export interface StoredMember {
  role: string;
  contactPerson: boolean;
  person: { id: string; formattedName?: string | null };
}

/** Why a projected row differs from what genesis holds. Absent on an unchanged row. */
export type MemberPendingKind = "add" | "remove" | "role" | "contact";

export interface ProjectedMember {
  id: string;
  name: string;
  role: CompanyRole | string;
  contactPerson: boolean;
  /** True for a person who is not in the company yet. */
  isNew: boolean;
  /** True for a stored member staged for removal — still listed, struck through in words. */
  isRemoved: boolean;
  /** Every way this row differs from the stored one, for the note under it. Empty when unchanged. */
  pending: MemberPendingKind[];
}

export interface CompanyMemberDraft {
  /** The roster as it will be once saved: stored rows folded with the staged intents. */
  projected: ProjectedMember[];
  /** True while anything is staged — what the SaveBar reads. */
  dirty: boolean;
  /** How many admins the projected roster has. Null while the stored roster is incomplete. */
  adminCount: number | null;
  /** How many contact persons the projected roster marks — the §4 cap counts this. */
  contactCount: number;
  /** True when the projected roster already marks the maximum. */
  contactSlotsFull: boolean;
  stageAdd: (person: { id: string; label: string }, role: CompanyRole) => void;
  stageRole: (personId: string, role: CompanyRole) => void;
  stageContact: (personId: string, contactPerson: boolean) => void;
  stageRemove: (personId: string) => void;
  /** Takes a row back to its stored state — and drops a staged add entirely. */
  unstage: (personId: string) => void;
  discard: () => void;
  /**
   * Applies every staged intent, in the one order that cannot lock the company out of itself.
   *
   * @param alsoAdd a person to add as well, for the one case the draft cannot see: picking somebody
   *   and pressing Save without pressing Add. `stageAdd` is a state update, so the `apply` in that
   *   render still closes over the draft as it was a moment ago.
   *
   * Resolves with the messages of whatever genesis refused; those intents STAY in the draft so they
   * can be corrected and saved again, and everything that succeeded is gone from it. An empty array
   * means everything landed.
   */
  apply: (alsoAdd?: { id: string; name: string; role: CompanyRole }) => Promise<string[]>;
}

interface DraftState {
  added: { id: string; name: string; role: CompanyRole }[];
  roles: Record<string, CompanyRole>;
  contacts: Record<string, boolean>;
  removed: string[];
}

const EMPTY: DraftState = { added: [], roles: {}, contacts: {}, removed: [] };

export function useCompanyMemberDraft(
  members: readonly StoredMember[],
  contactPersons: readonly { person: { id: string } }[],
  rosterComplete: boolean,
  admin: UseCompanyAdminResult,
): CompanyMemberDraft {
  const [draft, setDraft] = useState<DraftState>(EMPTY);

  const projected = useMemo<ProjectedMember[]>(() => {
    const storedContactIds = new Set(contactPersons.map((c) => c.person.id));
    const removed = new Set(draft.removed);

    const rows: ProjectedMember[] = members.map((member) => {
      const id = member.person.id;
      // THE STORED MARK COMES FROM THE UNPAGED CONTACT SET, not from the row. genesis serves the
      // complete contact list separately (@external-adr GENESIS-0029-the-member-strip-is-paged-and-the-contact-strip-is-not), and a row on a later page carries its own flag
      // only once that page is loaded — the set is the answer that is always complete.
      const storedContact = member.contactPerson || storedContactIds.has(id);
      const role = draft.roles[id] ?? member.role;
      const contactPerson = draft.contacts[id] ?? storedContact;
      const pending: MemberPendingKind[] = [];
      if (removed.has(id)) pending.push("remove");
      if (draft.roles[id] !== undefined && draft.roles[id] !== member.role) pending.push("role");
      if (draft.contacts[id] !== undefined && draft.contacts[id] !== storedContact) pending.push("contact");

      return {
        id,
        name: member.person.formattedName ?? "",
        role,
        contactPerson,
        isNew: false,
        isRemoved: removed.has(id),
        pending,
      };
    });

    // Appended rather than sorted in: a person staged for adding has no place in genesis's ordering
    // yet, and putting them at the end keeps the rows that ARE stored where the admin last saw them.
    for (const person of draft.added) {
      rows.push({
        id: person.id,
        name: person.name,
        role: draft.roles[person.id] ?? person.role,
        contactPerson: draft.contacts[person.id] ?? false,
        isNew: true,
        isRemoved: false,
        pending: ["add"],
      });
    }

    return rows;
  }, [members, contactPersons, draft]);

  const dirty =
    draft.added.length > 0 ||
    draft.removed.length > 0 ||
    Object.keys(draft.roles).length > 0 ||
    Object.keys(draft.contacts).length > 0;

  // Over the PROJECTION, not the stored rows: staging an add of a second admin is what makes
  // demoting the first one legitimate, and the admin has to be able to do both before saving either.
  // Still null on an incomplete roster, for the reason it always was — an admin on an unloaded page
  // is one genesis counts and this cannot see.
  const adminCount = rosterComplete
    ? projected.filter((row) => !row.isRemoved && row.role === COMPANY_ROLE_ADMIN).length
    : null;

  // OVER GENESIS'S COMPLETE CONTACT SET, with the draft folded on top — not over the rows on screen.
  // The roster is paged (@external-adr GENESIS-0029-the-member-strip-is-paged-and-the-contact-strip-is-not) and the contact list is not, so every marked person may sit on a
  // page nobody has loaded: counting the projection alone would answer "no contacts yet" and offer a
  // mark past the cap that the server refuses. This is the same reason the pre-staging code counted
  // `contactPersons` rather than the marked rows, and staging must not quietly lose it.
  const contactCount = useMemo(() => {
    const marked = new Set(contactPersons.map((contact) => contact.person.id));
    for (const [personId, isContact] of Object.entries(draft.contacts)) {
      if (isContact) marked.add(personId);
      else marked.delete(personId);
    }
    // A member staged for removal takes their mark with them.
    for (const personId of draft.removed) marked.delete(personId);

    return marked.size;
  }, [contactPersons, draft]);

  const stageAdd = useCallback((person: { id: string; label: string }, role: CompanyRole) => {
    setDraft((previous) => {
      if (previous.added.some((p) => p.id === person.id)) return previous;

      return {
        ...previous,
        // Staging an add for somebody staged for removal is the admin changing their mind: the
        // removal goes, and no add is needed because the row is already there.
        removed: previous.removed.filter((id) => id !== person.id),
        added: previous.removed.includes(person.id)
          ? previous.added
          : [...previous.added, { id: person.id, name: person.label, role }],
      };
    });
  }, []);

  const stageRole = useCallback((personId: string, role: CompanyRole) => {
    setDraft((previous) => ({
      ...previous,
      added: previous.added.map((p) => (p.id === personId ? { ...p, role } : p)),
      roles: { ...previous.roles, [personId]: role },
    }));
  }, []);

  const stageContact = useCallback((personId: string, contactPerson: boolean) => {
    setDraft((previous) => ({ ...previous, contacts: { ...previous.contacts, [personId]: contactPerson } }));
  }, []);

  const stageRemove = useCallback((personId: string) => {
    setDraft((previous) =>
      // Removing a person who was only staged for adding drops the intent rather than recording a
      // removal genesis would refuse — there is nothing there to remove.
      previous.added.some((p) => p.id === personId)
        ? { ...previous, added: previous.added.filter((p) => p.id !== personId) }
        : { ...previous, removed: previous.removed.includes(personId) ? previous.removed : [...previous.removed, personId] },
    );
  }, []);

  const unstage = useCallback((personId: string) => {
    setDraft((previous) => {
      const roles = { ...previous.roles };
      const contacts = { ...previous.contacts };
      delete roles[personId];
      delete contacts[personId];

      return {
        added: previous.added.filter((p) => p.id !== personId),
        removed: previous.removed.filter((id) => id !== personId),
        roles,
        contacts,
      };
    });
  }, []);

  const discard = useCallback(() => setDraft(EMPTY), []);

  const apply = useCallback(async (alsoAdd?: { id: string; name: string; role: CompanyRole }): Promise<string[]> => {
    // ADDS, THEN ROLES, THEN MARKS, THEN REMOVALS — and the order is the whole reason this is one
    // function rather than four buttons. genesis refuses any write that would leave the company
    // without an admin (@external-adr GENESIS-0023-company-membership-is-the-per-company-authorization §6), evaluating it against the state AT THAT MOMENT. An admin who
    // stages "add Bea as admin" and "remove me" means both together; applied in the other order the
    // removal is refused, correctly and uselessly. Marks come after adds so a newly added person can
    // be marked in the same save.
    const failures: string[] = [];
    const appliedAdds = new Set<string>();
    const appliedRoles = new Set<string>();
    const appliedContacts = new Set<string>();
    const appliedRemovals = new Set<string>();

    const additions = alsoAdd && !draft.added.some((p) => p.id === alsoAdd.id)
      ? [...draft.added, alsoAdd]
      : draft.added;

    for (const person of additions) {
      const result = await admin.addMember(person.id, draft.roles[person.id] ?? person.role);
      if (result.ok) appliedAdds.add(person.id);
      else failures.push(result.message);
    }

    for (const [personId, role] of Object.entries(draft.roles)) {
      // A staged add carries its role in the add itself, so a role staged for a person who was just
      // added is already written — and re-sending it would be a second write with nothing to change.
      if (appliedAdds.has(personId)) {
        appliedRoles.add(personId);
        continue;
      }
      if (additions.some((p) => p.id === personId)) continue;
      const result = await admin.setMemberRole(personId, role);
      if (result.ok) appliedRoles.add(personId);
      else failures.push(result.message);
    }

    for (const [personId, contactPerson] of Object.entries(draft.contacts)) {
      // Only for somebody who is actually in the company: a mark staged on a person whose add was
      // just refused has nothing to attach to, and genesis would answer with a second, confusing
      // refusal about a person it does not have.
      if (additions.some((p) => p.id === personId) && !appliedAdds.has(personId)) continue;
      const result = await admin.setContactPerson(personId, contactPerson);
      if (result.ok) appliedContacts.add(personId);
      else failures.push(result.message);
    }

    for (const personId of draft.removed) {
      const result = await admin.removeMember(personId);
      if (result.ok) appliedRemovals.add(personId);
      else failures.push(result.message);
    }

    // WHAT SUCCEEDED LEAVES THE DRAFT, WHAT WAS REFUSED STAYS IN IT. A save that half-applied and
    // then dropped everything would leave the admin unable to see what still needs doing, and a save
    // that kept the applied intents would try them again on the next press.
    setDraft((previous) => ({
      added: previous.added.filter((p) => !appliedAdds.has(p.id)),
      removed: previous.removed.filter((id) => !appliedRemovals.has(id)),
      roles: Object.fromEntries(
        Object.entries(previous.roles).filter(([id]) => !appliedRoles.has(id)),
      ),
      contacts: Object.fromEntries(
        Object.entries(previous.contacts).filter(([id]) => !appliedContacts.has(id)),
      ),
    }));

    return failures;
  }, [admin, draft]);

  return {
    projected,
    dirty,
    adminCount,
    contactCount,
    contactSlotsFull: contactCount >= MAX_CONTACT_PERSONS,
    stageAdd,
    stageRole,
    stageContact,
    stageRemove,
    unstage,
    discard,
    apply,
  };
}
